import React, { type Ref } from 'react'

import { Button, type ButtonProps, Icon } from '../../module'
import styles from './_banner.module.scss'

export type BannerProps = {
  /** Primary text to display */
  primaryText: string
  /** Secondary text to display */
  secondaryText: string
  /** Type of Banner */
  type: 'informational' | 'issue'
  /** Class to be applied to the component */
  className?: string
  /** Optional Button to be used in the Banner. It appears on the right side of the Banner. */
  buttonProps?: ButtonProps
  /** Optionally hide the info Icon */
  hideInfoIcon?: boolean
  /** Optionally show a close button. This function is called when the close button is clicked. */
  closeButtonCallout?: () => void
  /** Optional prop to add a test id to the Banner for QA testing */
  qaTestId?: string
}

const Banner = ({
  primaryText,
  secondaryText,
  className = '',
  type = 'informational',
  hideInfoIcon,
  closeButtonCallout,
  buttonProps,
  ref,
  qaTestId = 'banner',
}: BannerProps & {
  ref?: Ref<HTMLDivElement | null>
}): React.JSX.Element => {
  return (
    <div
      id='banner'
      className={`${styles.banner} ${className} ${styles[type]}`}
      ref={ref as Ref<HTMLDivElement>}
      qa-test-id={qaTestId}
    >
      <div className={styles.content}>
        {!hideInfoIcon ? (
          <Icon icon='info' iconSize='16px' color='white' />
        ) : null}
        <div className={styles.textAndButton}>
          <div>
            <div className={styles.primaryText}>{primaryText}</div>
            <div
              className={styles.secondaryText}
              dangerouslySetInnerHTML={{ __html: secondaryText }}
            />
          </div>
          {buttonProps || closeButtonCallout ? (
            <div className={styles.buttonContainer}>
              {buttonProps ? (
                <Button
                  {...buttonProps}
                  styleType='text-blue'
                  className={styles.button}
                />
              ) : null}
              {closeButtonCallout ? (
                <Button
                  as='unstyled'
                  onClick={closeButtonCallout}
                  className={styles.closeButton}
                >
                  <Icon icon='x' iconSize='12px' color='white' />
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

Banner.displayName = 'Banner'
export default Banner
